<template>
  <div class="stat-card" :class="[size, variant]">
    <h3>{{ title }}</h3>
    <p class="stat-number">{{ displayValue }}</p>
    <p v-if="detail" class="stat-detail">{{ detail }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  title: string;
  value: string | number;
  detail?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  formatter?: (value: string | number) => string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'medium',
  variant: 'default',
});

const displayValue = computed(() => {
  if (props.formatter) {
    return props.formatter(props.value);
  }
  return props.value.toString();
});
</script>

<style scoped>
.stat-card {
  background: white;
  padding: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 2px solid transparent;
  transition: all 0.2s ease;
}

.stat-card:hover {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.stat-card h3 {
  margin: 0 0 0.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-number {
  margin: 0;
  font-size: 2.5rem;
  font-weight: bold;
  color: #3b82f6;
  line-height: 1;
}

.stat-detail {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: normal;
}

/* Size variants */
.small .stat-number {
  font-size: 1.75rem;
}

.small {
  padding: 0.75rem;
}

.large .stat-number {
  font-size: 3rem;
}

.large {
  padding: 1.5rem;
}

/* Color variants */
.primary {
  border-color: #3b82f6;
}

.primary .stat-number {
  color: #3b82f6;
}

.success {
  border-color: #10b981;
}

.success .stat-number {
  color: #10b981;
}

.warning {
  border-color: #f59e0b;
}

.warning .stat-number {
  color: #f59e0b;
}

.danger {
  border-color: #ef4444;
}

.danger .stat-number {
  color: #ef4444;
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .stat-card {
    padding: 0.75rem;
  }

  .small {
    padding: 0.5rem;
  }

  .large {
    padding: 1rem;
  }

  .stat-number {
    font-size: 1.75rem;
  }

  .small .stat-number {
    font-size: 1.5rem;
  }

  .large .stat-number {
    font-size: 2rem;
  }
}
</style>
